/* MY PUMPS — the pump company's own units (boom/line pumps + operators). Each one gets a
   permanent QR the owner texts or prints for that operator; scanning it shows only the jobs
   that pump has been allocated to by the concrete plant. */
import { useCallback, useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { Construction, Plus, QrCode, Trash2, Copy, Printer, ExternalLink, X, MessageSquare } from "lucide-react";
import { api } from "@/lib/api";
import { printQrPage } from "@/lib/printQr";

const inputCls = "w-full bg-[#0B0D10] border border-[#2C313A] focus:border-[#38BDF8] outline-none text-white px-3 py-2.5 text-sm";
const empty = { name: "", operator_name: "", phone: "", paired_truck_id: "", active: true };

function QrPanel({ unit, onClose }) {
  const print = () => printQrPage({
    title: `Pump Tracker · ${unit.name}`,
    tagLine: "ARMORBATCH GLOBAL · PUMP TRACKER",
    heading: `${unit.company} · ${unit.name}`,
    description: "Scan this with your phone camera to see every concrete truck allocated to this pump today — live GPS, ETA and volume delivered. The link is permanent, so bookmark it.",
    footer: "© ARMORBATCH GLOBAL · Live pump tracker",
    url: unit.url,
    svgElementId: "unit-qr-svg",
  });
  const smsBody = encodeURIComponent(`Your pump tracker for ${unit.name} — live truck ETAs: ${unit.url}`);
  return (
    <div className="border border-[#38BDF8] bg-[#12151A] p-4 mt-3 flex flex-col sm:flex-row gap-4" data-testid="unit-qr-panel">
      <div className="p-3 bg-white self-start"><QRCodeSVG id="unit-qr-svg" value={unit.url} size={170} level="M" /></div>
      <div className="min-w-0 flex-1 space-y-2">
        <div className="text-[10px] tracking-widest uppercase text-[#8B909A]">Hand this to {unit.operator_name || "your operator"}</div>
        <div className="mono text-[11px] break-all text-[#38BDF8] border border-[#2C313A] px-2 py-1.5" data-testid="unit-qr-url">{unit.url}</div>
        <div className="flex flex-wrap gap-2">
          <button type="button" data-testid="unit-qr-copy" onClick={() => { navigator.clipboard?.writeText(unit.url); toast.success("Link copied"); }}
            className="mono text-[10px] uppercase tracking-widest border border-[#2C313A] px-3 py-1.5 text-[#A1A6B0] hover:text-white inline-flex items-center gap-1"><Copy className="w-3 h-3" /> Copy</button>
          <button type="button" data-testid="unit-qr-print" onClick={print}
            className="mono text-[10px] uppercase tracking-widest border border-[#2C313A] px-3 py-1.5 text-[#A1A6B0] hover:text-white inline-flex items-center gap-1"><Printer className="w-3 h-3" /> Print</button>
          {unit.phone && (
            <a href={`sms:${unit.phone}?&body=${smsBody}`} data-testid="unit-qr-sms"
              className="mono text-[10px] uppercase tracking-widest border border-[#2C313A] px-3 py-1.5 text-[#A1A6B0] hover:text-white inline-flex items-center gap-1"><MessageSquare className="w-3 h-3" /> Text {unit.phone}</a>
          )}
          <a href={unit.url} target="_blank" rel="noreferrer" data-testid="unit-qr-open"
            className="mono text-[10px] uppercase tracking-widest border border-[#2C313A] px-3 py-1.5 text-[#A1A6B0] hover:text-white inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Open</a>
        </div>
      </div>
      <button type="button" data-testid="unit-qr-close" onClick={onClose} className="self-start p-1 text-[#8B909A] hover:text-white"><X className="w-4 h-4" /></button>
    </div>
  );
}

export default function PartnerPumps() {
  const [units, setUnits] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [editing, setEditing] = useState(null);     // unit id or "new"
  const [f, setF] = useState(empty);
  const [qrId, setQrId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [u, t] = await Promise.all([api.get("/partner/units"), api.get("/partner/trucks")]);
      setUnits(u.data.units || []);
      setTrucks(t.data.trucks || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load your pumps"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing("new"); setF(empty); };
  const openEdit = (u) => { setEditing(u.id); setF({ ...empty, ...u }); };

  const save = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) return toast.error("Give the pump a name, e.g. Boom 32m");
    setBusy(true);
    try {
      if (editing === "new") await api.post("/partner/units", f);
      else await api.put(`/partner/units/${editing}`, f);
      toast.success("Saved");
      setEditing(null); load();
    } catch (ex) { toast.error(ex.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };

  const del = async (u) => {
    if (!window.confirm(`Remove ${u.name}? Its QR link will stop working.`)) return;
    try { await api.delete(`/partner/units/${u.id}`); toast.success("Removed"); load(); }
    catch (ex) { toast.error(ex.response?.data?.detail || "Could not remove"); }
  };

  return (
    <div className="p-4 lg:p-8 space-y-5 max-w-4xl" data-testid="partner-pumps-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.25em] text-[#8B909A]">MY FLEET</div>
          <h1 className="heading text-2xl sm:text-3xl font-black uppercase text-white">My <span className="text-[#38BDF8]">Pumps</span></h1>
          <p className="text-[13px] text-[#A1A6B0] mt-1 max-w-2xl">
            Add each pump you run. Every one gets its own permanent QR you can text or print for that
            operator — they'll see only the trucks heading to the jobs that pump is booked on. Tell your
            concrete plant which pump you're sending and they allocate it on the order.
          </p>
        </div>
        <button data-testid="unit-new-btn" onClick={openNew}
          className="bg-[#38BDF8] text-[#0B0D10] hover:bg-[#7DD3FC] font-black uppercase tracking-widest text-xs px-4 py-2.5 inline-flex items-center gap-1"><Plus className="w-4 h-4" /> Add pump</button>
      </div>

      {editing && (
        <form onSubmit={save} className="bg-[#12151A] border border-[#38BDF8]/40 p-5 space-y-3" data-testid="unit-form">
          <div className="text-[10px] tracking-widest uppercase text-[#8B909A]">{editing === "new" ? "New pump" : "Edit pump"}</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <label><span className="block text-[10px] tracking-widest uppercase text-[#8B909A] mb-1">Pump name *</span>
              <input data-testid="unit-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Boom 32m" className={inputCls} /></label>
            <label><span className="block text-[10px] tracking-widest uppercase text-[#8B909A] mb-1">Operator</span>
              <input data-testid="unit-operator" value={f.operator_name} onChange={(e) => setF({ ...f, operator_name: e.target.value })} className={inputCls} /></label>
            <label><span className="block text-[10px] tracking-widest uppercase text-[#8B909A] mb-1">Operator mobile</span>
              <input data-testid="unit-phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className={inputCls} /></label>
            <label><span className="block text-[10px] tracking-widest uppercase text-[#8B909A] mb-1">Usually pours with (optional)</span>
              <select data-testid="unit-truck" value={f.paired_truck_id || ""} onChange={(e) => setF({ ...f, paired_truck_id: e.target.value })} className={inputCls}>
                <option value="">— no truck paired —</option>
                {trucks.map((t) => <option key={t.id} value={t.id}>{t.number}{t.rego ? ` · ${t.rego}` : ""} · {t.supplier}</option>)}
              </select></label>
          </div>
          <div className="flex gap-2">
            <button type="submit" disabled={busy} data-testid="unit-save" className="bg-[#38BDF8] text-[#0B0D10] hover:bg-[#7DD3FC] font-black uppercase tracking-widest text-xs px-4 py-2.5 disabled:opacity-50">{busy ? "Saving…" : "Save pump"}</button>
            <button type="button" data-testid="unit-cancel" onClick={() => setEditing(null)} className="mono text-[11px] uppercase tracking-widest border border-[#2C313A] px-4 text-[#A1A6B0] hover:text-white">Cancel</button>
          </div>
        </form>
      )}

      {units.length === 0 && !editing && (
        <div className="border border-dashed border-[#2C313A] p-10 text-center" data-testid="units-empty">
          <Construction className="w-9 h-9 mx-auto text-[#38BDF8]" />
          <div className="heading text-lg font-black uppercase text-white mt-3">No pumps added yet</div>
          <p className="text-[13px] text-[#A1A6B0] mt-2">Add your first pump to get a QR code for its operator.</p>
        </div>
      )}

      <div className="space-y-3" data-testid="units-list">
        {units.map((u) => (
          <div key={u.id} className="bg-[#12151A] border border-[#22262E] p-4" data-testid={`unit-card-${u.id}`}>
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 bg-[#38BDF8] flex items-center justify-center shrink-0"><Construction className="w-4 h-4 text-[#0B0D10]" /></div>
              <div className="min-w-0">
                <button type="button" onClick={() => openEdit(u)} data-testid={`unit-edit-${u.id}`} className="heading text-lg font-black uppercase text-white truncate hover:text-[#38BDF8]">{u.name}</button>
                <div className="mono text-[11px] text-[#A1A6B0]">{u.operator_name || "operator TBA"}{u.phone ? ` · ${u.phone}` : ""}</div>
              </div>
              <div className="ml-auto flex items-center gap-2">
                <button type="button" data-testid={`unit-qr-btn-${u.id}`} onClick={() => setQrId(qrId === u.id ? null : u.id)}
                  className="mono text-[10px] uppercase tracking-widest border border-[#38BDF8] text-[#38BDF8] hover:bg-[#38BDF8] hover:text-black px-3 py-1.5 inline-flex items-center gap-1"><QrCode className="w-3 h-3" /> QR</button>
                <button type="button" data-testid={`unit-del-${u.id}`} onClick={() => del(u)} className="p-2 border border-[#2C313A] text-[#8B909A] hover:border-red-500 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
            {qrId === u.id && <QrPanel unit={u} onClose={() => setQrId(null)} />}
          </div>
        ))}
      </div>
    </div>
  );
}
